import React, { useState, useEffect, useMemo, useCallback, useRef, } from "react";
import { useIntl } from "react-intl";
import { Accordion, AccordionSet, Checkbox, Loading, Modal, ModalFooter, Button, Select, TextField, TextArea, MessageBanner, IconButton, Row, Col, } from "@folio/stripes/components";
import { CHANNEL_LABEL_IDS, UrgenceSkipReasonEnum, urgenceTitleKey, } from "../../types/enforcement";
import { summarizeSkippedRecords } from "../../utils/urgenceSkipSummary";
import { VOLUME_COLORS } from "../../utils/volumeConstants";
import { RichTextEditor } from "../inputs/RichTextEditor";
import { isValidEmail } from "../../utils/validationUtils";
import { isBodyEmpty, htmlToPlainText } from "../../utils/htmlText";
import { copyToClipboard } from "../../utils/clipboard";
import styles from "./SendEnforcementDialog.module.css";
var CopyLabel = function (_a) {
    var text = _a.text, value = _a.value, ariaLabel = _a.ariaLabel;
    var formatMessage = useIntl().formatMessage;
    var _b = useState(false), copied = _b[0], setCopied = _b[1];
    var timeoutRef = useRef();
    var wrapRef = useRef(null);
    useEffect(function () { return function () {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
    }; }, []);
    var handleCopy = useCallback(function (e) {
        var _a;
        e.preventDefault();
        e.stopPropagation();
        copyToClipboard(value, (_a = wrapRef.current) !== null && _a !== void 0 ? _a : document.body);
        setCopied(true);
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(function () { return setCopied(false); }, 1500);
    }, [value]);
    return (React.createElement("span", { className: styles.copyLabel },
        text,
        React.createElement("span", { className: styles.copyIconWrap, ref: wrapRef },
            React.createElement(IconButton, { icon: "duplicate", ariaLabel: ariaLabel, onClick: handleCopy }),
            copied && (React.createElement("span", { className: styles.copiedToast, role: "status" }, formatMessage({ id: "ui-br-legaldeposits.enforcement.copied" }))))));
};
var ENFORCED_BADGE_STYLE = {
    background: VOLUME_COLORS.urged.bg,
    color: VOLUME_COLORS.urged.color,
};
var FRESH_BADGE_STYLE = {
    background: VOLUME_COLORS.missing.bg,
    color: VOLUME_COLORS.missing.color,
};
var SELECTION_LABEL_IDS = {
    URGENCE: {
        enforced: "ui-br-legaldeposits.urgence.group.urgedBefore",
        fresh: "ui-br-legaldeposits.urgence.group.notUrged",
        badgeOn: "ui-br-legaldeposits.urgence.badge.sentOn",
        badgeActive: "ui-br-legaldeposits.urgence.badge.activeNoSend",
        badgeSent: "ui-br-legaldeposits.urgence.badge.blockedSent",
    },
    REMINDER: {
        enforced: "ui-br-legaldeposits.reminder.group.sentBefore",
        fresh: "ui-br-legaldeposits.reminder.group.notSent",
        badgeOn: "ui-br-legaldeposits.reminder.badge.sentOn",
        badgeActive: "ui-br-legaldeposits.reminder.badge.activeNoSend",
        badgeSent: "ui-br-legaldeposits.reminder.badge.blockedSent",
    },
};
var NOTE_ID = "__NOTE__";
var CUSTOM_EMAIL_ID = "__CUSTOM_EMAIL__";
export var SendEnforcementDialog = function (_a) {
    var _b, _c, _d;
    var open = _a.open, onClose = _a.onClose, onBack = _a.onBack, onSubmit = _a.onSubmit, preview = _a.preview, _e = _a.isLoading, isLoading = _e === void 0 ? false : _e, _f = _a.dialogTitleId, dialogTitleId = _f === void 0 ? "ui-br-legaldeposits.enforcement.send.title" : _f, _g = _a.submitEmailLabelId, submitEmailLabelId = _g === void 0 ? "ui-br-legaldeposits.button.send" : _g, _h = _a.submitNoteLabelId, submitNoteLabelId = _h === void 0 ? "ui-br-legaldeposits.button.recordUrgence" : _h, _j = _a.showTitleCount, showTitleCount = _j === void 0 ? true : _j, selection = _a.selection, _k = _a.phase, phase = _k === void 0 ? "message" : _k, notice = _a.notice;
    var _l = useIntl(), formatMessage = _l.formatMessage, formatDate = _l.formatDate;
    var _m = useState(new Set()), selectedTitleKeys = _m[0], setSelectedTitleKeys = _m[1];
    var _o = useState(true), enforcedOpen = _o[0], setEnforcedOpen = _o[1];
    var _p = useState(true), freshOpen = _p[0], setFreshOpen = _p[1];
    var _q = useState(false), selectionInitialized = _q[0], setSelectionInitialized = _q[1];
    var _r = useState(""), selectedContactId = _r[0], setSelectedContactId = _r[1];
    var _s = useState(""), subject = _s[0], setSubject = _s[1];
    var _t = useState(""), body = _t[0], setBody = _t[1];
    var _u = useState(""), notes = _u[0], setNotes = _u[1];
    var _v = useState(""), customEmail = _v[0], setCustomEmail = _v[1];
    var _w = useState(false), subjectTouched = _w[0], setSubjectTouched = _w[1];
    var _x = useState(false), bodyTouched = _x[0], setBodyTouched = _x[1];
    var customEmailWrapperRef = useRef(null);
    useEffect(function () {
        var el = customEmailWrapperRef.current;
        if (!el)
            return undefined;
        var handler = function (e) {
            if (!e.key)
                e.stopPropagation();
        };
        el.addEventListener("keydown", handler, true);
        return function () { return el.removeEventListener("keydown", handler, true); };
    }, [selectedContactId]);
    var contacts = useMemo(function () { var _a; return ((_a = preview === null || preview === void 0 ? void 0 : preview.contacts) !== null && _a !== void 0 ? _a : []).filter(function (c) { return c.type !== "WEB"; }); }, [preview]);
    var groupedOptions = useMemo(function () {
        var _a;
        var groups = {};
        for (var _i = 0, contacts_1 = contacts; _i < contacts_1.length; _i++) {
            var c = contacts_1[_i];
            var group = (_a = groups[c.type]) !== null && _a !== void 0 ? _a : [];
            group.push(c);
            groups[c.type] = group;
        }
        var options = [];
        if (groups.EMAIL) {
            var typeLabel = formatMessage({ id: CHANNEL_LABEL_IDS.EMAIL });
            for (var _b = 0, _c = groups.EMAIL; _b < _c.length; _b++) {
                var item = _c[_b];
                var suffix = item.info ? " (".concat(item.info, ")") : "";
                options.push({
                    value: item.id,
                    label: "".concat(typeLabel, ": ").concat(item.value).concat(suffix),
                });
            }
        }
        options.push({
            value: CUSTOM_EMAIL_ID,
            label: formatMessage({
                id: "ui-br-legaldeposits.enforcement.field.customEmail",
            }),
        });
        for (var _d = 0, _e = Object.entries(groups).filter(function (_a) {
            var groupType = _a[0];
            return groupType !== "EMAIL";
        }); _d < _e.length; _d++) {
            var _f = _e[_d], type = _f[0], items = _f[1];
            var channelType = type;
            var typeLabel = CHANNEL_LABEL_IDS[channelType]
                ? formatMessage({ id: CHANNEL_LABEL_IDS[channelType] })
                : type;
            for (var _g = 0, items_1 = items; _g < items_1.length; _g++) {
                var item = items_1[_g];
                var suffix = item.info ? " (".concat(item.info, ")") : "";
                options.push({
                    value: item.id,
                    label: "".concat(typeLabel, ": ").concat(item.value).concat(suffix),
                });
            }
        }
        options.push({
            value: NOTE_ID,
            label: formatMessage({ id: CHANNEL_LABEL_IDS.NOTE }),
        });
        return options;
    }, [contacts, formatMessage]);
    var isCustomEmail = selectedContactId === CUSTOM_EMAIL_ID;
    var isNote = selectedContactId === NOTE_ID;
    var selectedContact = useMemo(function () {
        var _a;
        return isNote || isCustomEmail
            ? null
            : ((_a = contacts.find(function (c) { return c.id === selectedContactId; })) !== null && _a !== void 0 ? _a : null);
    }, [isNote, isCustomEmail, contacts, selectedContactId]);
    var isEmail = (selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.type) === "EMAIL" || isCustomEmail;
    var isDataBox = (selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.type) === "DATA_BOX";
    var showMessageText = isEmail || isDataBox;
    useEffect(function () {
        var _a, _b;
        if (preview) {
            setSubject((_a = preview.subject) !== null && _a !== void 0 ? _a : "");
            setBody((_b = preview.body) !== null && _b !== void 0 ? _b : "");
        }
    }, [preview]);
    useEffect(function () {
        if (!open) {
            setSelectedContactId("");
            setSubject("");
            setBody("");
            setNotes("");
            setCustomEmail("");
            setSubjectTouched(false);
            setBodyTouched(false);
            setSelectedTitleKeys(new Set());
            setSelectionInitialized(false);
            setEnforcedOpen(true);
            setFreshOpen(true);
        }
    }, [open]);
    useEffect(function () {
        if (!open || selectedContactId || preview === null)
            return;
        var primary = contacts.find(function (c) { return c.type === "EMAIL" && c.isPrimary; });
        var firstEmail = contacts.find(function (c) { return c.type === "EMAIL"; });
        var emailContact = primary !== null && primary !== void 0 ? primary : firstEmail;
        setSelectedContactId(emailContact ? emailContact.id : CUSTOM_EMAIL_ID);
    }, [open, preview, contacts, selectedContactId]);
    var blockedKeys = useMemo(function () {
        if (!selection)
            return new Set();
        var blockedRecords = new Set(selection.skipped.map(function (record) { return record.depositRecordId; }));
        return new Set(selection.titles
            .filter(function (title) {
            return title.depositRecordIds.every(function (id) { return blockedRecords.has(id); });
        })
            .map(urgenceTitleKey));
    }, [selection]);
    var enforcedTitles = useMemo(function () { var _a; return ((_a = selection === null || selection === void 0 ? void 0 : selection.titles) !== null && _a !== void 0 ? _a : []).filter(function (t) { return t.previouslyEnforced; }); }, [selection]);
    var freshTitles = useMemo(function () { var _a; return ((_a = selection === null || selection === void 0 ? void 0 : selection.titles) !== null && _a !== void 0 ? _a : []).filter(function (t) { return !t.previouslyEnforced; }); }, [selection]);
    // The preview arrives after the dialog opens, so an empty titles list is "not loaded yet",
    // not "nothing to preselect" — initializing on it would leave every title unticked.
    useEffect(function () {
        if (!open ||
            !selection ||
            selection.titles.length === 0 ||
            selectionInitialized) {
            return;
        }
        setSelectedTitleKeys(new Set(selection.titles
            .filter(function (t) { return !t.previouslyEnforced; })
            .map(urgenceTitleKey)
            .filter(function (key) { return !blockedKeys.has(key); })));
        setSelectionInitialized(true);
    }, [open, selection, selectionInitialized, blockedKeys]);
    var toggleTitle = useCallback(function (key) {
        setSelectedTitleKeys(function (prev) {
            var next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            }
            else {
                next.add(key);
            }
            return next;
        });
    }, []);
    var selectedDepositRecordIds = useMemo(function () {
        var _a;
        return ((_a = selection === null || selection === void 0 ? void 0 : selection.titles) !== null && _a !== void 0 ? _a : [])
            .filter(function (t) { return selectedTitleKeys.has(urgenceTitleKey(t)); })
            .flatMap(function (t) { return t.depositRecordIds; });
    }, [selection, selectedTitleKeys]);
    var handleContinue = useCallback(function () { return selection === null || selection === void 0 ? void 0 : selection.onContinue(selectedDepositRecordIds); }, [selection, selectedDepositRecordIds]);
    var forecastSummary = useMemo(function () {
        var _a;
        return summarizeSkippedRecords((_a = selection === null || selection === void 0 ? void 0 : selection.skipped) !== null && _a !== void 0 ? _a : preview === null || preview === void 0 ? void 0 : preview.skipped, formatMessage);
    }, [selection, preview, formatMessage]);
    var handleSubmit = useCallback(function () {
        var _a, _b;
        onSubmit({
            communicationType: isNote
                ? "NOTE"
                : isCustomEmail
                    ? "EMAIL"
                    : ((_a = selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.type) !== null && _a !== void 0 ? _a : "NOTE"),
            subject: showMessageText ? subject : "",
            body: showMessageText ? body : "",
            recipientAddress: isCustomEmail
                ? customEmail
                : ((_b = selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.value) !== null && _b !== void 0 ? _b : ""),
            notes: notes,
        });
    }, [
        isNote,
        isCustomEmail,
        showMessageText,
        selectedContact,
        subject,
        body,
        customEmail,
        notes,
        onSubmit,
    ]);
    var noApplicableTitles = preview !== null && preview.titleCount === 0;
    var isValid = !noApplicableTitles &&
        !!selectedContactId &&
        (showMessageText
            ? (isCustomEmail ? isValidEmail(customEmail) : true) &&
                subject.trim().length > 0 &&
                !isBodyEmpty(body)
            : notes.trim().length > 0);
    var submitButtonLabel = isEmail
        ? formatMessage({ id: submitEmailLabelId })
        : formatMessage({ id: submitNoteLabelId });
    var selecting = !!selection && phase === "select";
    var labels = SELECTION_LABEL_IDS[(_b = selection === null || selection === void 0 ? void 0 : selection.kind) !== null && _b !== void 0 ? _b : "URGENCE"];
    var badgeText = function (title) {
        return title.lastSentDate
            ? formatMessage({ id: labels.badgeOn }, { date: formatDate(title.lastSentDate) })
            : formatMessage({ id: labels.badgeActive });
    };
    // A title nothing can be sent for says so on its own row: a blocked one that was never enforced
    // would otherwise sit in "without a reminder" wearing a badge that contradicts why it is greyed
    // out, and the librarian would have no way to find out what is wrong with it.
    var blockedReasonText = function (title) {
        var _a, _b;
        if (!blockedKeys.has(urgenceTitleKey(title))) {
            return null;
        }
        var records = new Set(title.depositRecordIds);
        var reason = (_b = ((_a = selection === null || selection === void 0 ? void 0 : selection.skipped) !== null && _a !== void 0 ? _a : []).find(function (s) {
            return records.has(s.depositRecordId);
        })) === null || _b === void 0 ? void 0 : _b.reason;
        return reason === UrgenceSkipReasonEnum.REMINDER_ALREADY_SENT
            ? formatMessage({ id: labels.badgeSent })
            : formatMessage({ id: labels.badgeActive });
    };
    var renderTitleRow = function (title, badgeStyle, text) {
        var key = urgenceTitleKey(title);
        var blockedText = blockedReasonText(title);
        return (React.createElement(Checkbox, { key: key, checked: selectedTitleKeys.has(key), disabled: !!blockedText, onChange: function () { return toggleTitle(key); }, label: React.createElement("span", { className: styles.titleRow },
                React.createElement("span", null, title.isbn
                    ? "".concat(title.titleName, " \u00B7 ").concat(title.isbn)
                    : title.titleName),
                React.createElement("span", { className: styles.badge, style: blockedText ? ENFORCED_BADGE_STYLE : badgeStyle }, blockedText !== null && blockedText !== void 0 ? blockedText : text)) }));
    };
    return (React.createElement(Modal, { open: open, label: formatMessage({ id: dialogTitleId }), onClose: onClose, size: "large", footer: React.createElement(ModalFooter, null,
            selecting ? (React.createElement(Button, { buttonStyle: "primary", onClick: handleContinue, disabled: selectedDepositRecordIds.length === 0 }, formatMessage({ id: "ui-br-legaldeposits.button.continue" }))) : (React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit, disabled: !isValid || isLoading }, submitButtonLabel)),
            !selecting && onBack && (React.createElement(Button, { onClick: onBack }, formatMessage({ id: "ui-br-legaldeposits.button.back" }))),
            React.createElement(Button, { onClick: onClose }, formatMessage({ id: "ui-br-legaldeposits.button.cancel" }))) }, selecting ? (isLoading && selection.titles.length === 0 ? (React.createElement(Loading, null)) : selection.titles.length === 0 ? (React.createElement(MessageBanner, { type: "warning" }, formatMessage({
        id: "ui-br-legaldeposits.enforcement.send.nothingToSelect",
    }))) : (React.createElement(React.Fragment, null,
        React.createElement(AccordionSet, null,
            enforcedTitles.length > 0 && (React.createElement(Accordion, { id: "enforcement-select-enforced", label: formatMessage({ id: labels.enforced }, { count: enforcedTitles.length }), open: enforcedOpen, onToggle: function () { return setEnforcedOpen(function (prev) { return !prev; }); } }, enforcedTitles.map(function (t) {
                return renderTitleRow(t, ENFORCED_BADGE_STYLE, badgeText(t));
            }))),
            freshTitles.length > 0 && (React.createElement(Accordion, { id: "enforcement-select-fresh", label: formatMessage({ id: labels.fresh }, { count: freshTitles.length }), open: freshOpen, onToggle: function () { return setFreshOpen(function (prev) { return !prev; }); } }, freshTitles.map(function (t) {
                return renderTitleRow(t, FRESH_BADGE_STYLE, formatMessage({
                    id: "ui-br-legaldeposits.preLitigation.badge.notUrged",
                }));
            })))),
        forecastSummary && (React.createElement("div", { className: styles.forecast, "data-testid": "urgence-forecast-skipped" },
            React.createElement(MessageBanner, { type: "warning" }, formatMessage({
                id: "ui-br-legaldeposits.enforcement.send.forecastSkipped",
            }, { summary: forecastSummary }))))))) : noApplicableTitles ? (React.createElement(React.Fragment, null,
        React.createElement(MessageBanner, { type: "warning" }, formatMessage({
            id: "ui-br-legaldeposits.bulk.validation.noApplicableTitles",
        })),
        forecastSummary && (React.createElement("div", { className: styles.forecast, "data-testid": "urgence-forecast-skipped" },
            React.createElement(MessageBanner, { type: "warning" }, formatMessage({
                id: "ui-br-legaldeposits.enforcement.send.forecastSkipped",
            }, { summary: forecastSummary })))))) : (React.createElement(React.Fragment, null,
        notice,
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(Select, { label: formatMessage({
                        id: "ui-br-legaldeposits.enforcement.field.contact",
                    }), dataOptions: groupedOptions, value: selectedContactId, onChange: function (e) {
                        return setSelectedContactId(e.target.value);
                    } }))),
        isCustomEmail && (React.createElement("div", { ref: customEmailWrapperRef },
            React.createElement(TextField, { id: "custom-email-address", name: "customEmail", label: formatMessage({
                    id: "ui-br-legaldeposits.enforcement.field.customEmailAddress",
                }), value: customEmail, onChange: function (e) {
                    return setCustomEmail(e.target.value);
                }, error: customEmail && !isValidEmail(customEmail)
                    ? formatMessage({
                        id: "ui-br-legaldeposits.publishers.validation.contact.email",
                    })
                    : undefined, required: true }))),
        isDataBox && (React.createElement(TextField, { id: "data-box-id", name: "dataBoxId", label: React.createElement(CopyLabel, { text: formatMessage({
                    id: "ui-br-legaldeposits.enforcement.field.dataBoxAddress",
                }), value: (_c = selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.value) !== null && _c !== void 0 ? _c : "", ariaLabel: formatMessage({
                    id: "ui-br-legaldeposits.enforcement.copy",
                }) }), value: (_d = selectedContact === null || selectedContact === void 0 ? void 0 : selectedContact.value) !== null && _d !== void 0 ? _d : "", onChange: function () { return undefined; } })),
        showMessageText && (React.createElement(React.Fragment, null,
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(TextField, { label: isDataBox ? (React.createElement(CopyLabel, { text: formatMessage({
                                id: "ui-br-legaldeposits.enforcement.field.subject",
                            }), value: subject, ariaLabel: formatMessage({
                                id: "ui-br-legaldeposits.enforcement.copy",
                            }) })) : (formatMessage({
                            id: "ui-br-legaldeposits.enforcement.field.subject",
                        })), value: subject, onChange: function (e) {
                            return setSubject(e.target.value);
                        }, onBlur: function () { return setSubjectTouched(true); }, required: true, error: subjectTouched && subject.trim().length === 0
                            ? formatMessage({
                                id: "ui-br-legaldeposits.enforcement.validation.subjectRequired",
                            })
                            : undefined }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(RichTextEditor, { id: "enforcement-body", label: isDataBox ? (React.createElement(CopyLabel, { text: formatMessage({
                                id: "ui-br-legaldeposits.enforcement.field.body",
                            }), value: htmlToPlainText(body), ariaLabel: formatMessage({
                                id: "ui-br-legaldeposits.enforcement.copy",
                            }) })) : (formatMessage({
                            id: "ui-br-legaldeposits.enforcement.field.body",
                        })), value: body, onChange: function (html) {
                            setBody(html);
                            setBodyTouched(true);
                        }, required: true, error: bodyTouched && isBodyEmpty(body)
                            ? formatMessage({
                                id: "ui-br-legaldeposits.enforcement.validation.bodyRequired",
                            })
                            : undefined }))))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(TextArea, { label: "".concat(formatMessage({ id: "ui-br-legaldeposits.enforcement.field.notes" })).concat(!showMessageText ? " *" : ""), value: notes, onChange: function (e) {
                        return setNotes(e.target.value);
                    } }))),
        preview && showTitleCount && (React.createElement("div", { className: styles.previewInfo }, formatMessage({ id: "ui-br-legaldeposits.enforcement.send.titleCount" }, { count: preview.titleCount }))),
        selection && (React.createElement("div", { className: styles.backHint }, formatMessage({
            id: "ui-br-legaldeposits.enforcement.send.backDiscardsText",
        })))))));
};
